import React, { useState, useEffect } from 'react';
import { Save, X, Calendar, Tag as TagIcon } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { generateSitemap } from '../../../utils/generateSitemap';
import RichTextEditor from '../../../components/ui/RichTextEditor';

const NewsletterEditorForm = ({ 
  newsletter, 
  onSave, 
  onCancel,
  isSaving 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    tags: [],
    publishedAt: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    console.log('[NewsletterEditorForm] Newsletter prop changed:', newsletter ? newsletter?.id : 'NULL');
    
    if (newsletter) {
      console.log('[NewsletterEditorForm] Loading newsletter for edit:', {
        id: newsletter?.id,
        title: newsletter?.title
      });
      
      setFormData({
        title: newsletter?.title || '',
        slug: newsletter?.slug || '',
        excerpt: newsletter?.excerpt || '',
        content: newsletter?.content || '',
        tags: newsletter?.tags || [],
        publishedAt: newsletter?.publishedAt ? new Date(newsletter.publishedAt)?.toISOString()?.split('T')?.[0] : ''
      });
      setIsSlugManuallyEdited(true);
      setIsEditMode(true);
    } else {
      console.log('[NewsletterEditorForm] Creating new newsletter');
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        tags: [],
        publishedAt: new Date()?.toISOString()?.split('T')?.[0]
      });
      setIsSlugManuallyEdited(false);
    }
    setErrors({});
  }, [newsletter]);

  const generateSlug = (title) => {
    return title?.toLowerCase()?.replace(/[^\w\s-]/g, '')?.replace(/\s+/g, '-')?.replace(/-+/g, '-')?.trim();
  };

  const handleTitleChange = (e) => {
    const newTitle = e?.target?.value;
    setFormData(prev => ({
      ...prev,
      title: newTitle,
      slug: isSlugManuallyEdited ? prev?.slug : generateSlug(newTitle)
    }));
    if (errors?.title) {
      setErrors(prev => ({ ...prev, title: null }));
    }
  };

  const handleSlugChange = (e) => {
    const newSlug = e?.target?.value;
    setFormData(prev => ({ ...prev, slug: newSlug }));
    setIsSlugManuallyEdited(true);
    if (errors?.slug) {
      setErrors(prev => ({ ...prev, slug: null }));
    }
  };

  const handleAddTag = (e) => {
    if (e?.key === 'Enter' || e?.key === ',') {
      e?.preventDefault();
      const tag = tagInput?.trim();
      if (tag && !formData?.tags?.includes(tag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...(prev?.tags || []), tag]
        }));
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const handleImageInsert = (imageHtml) => {
    // Insert image HTML directly into the rich text editor content
    // Append at the end since React Quill doesn't expose cursor position easily
    const newContent = (formData?.content || '') + imageHtml;
    setFormData(prev => ({ ...prev, content: newContent }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData?.slug?.trim()) {
      newErrors.slug = 'Slug is required';
    }

    if (!formData?.publishedAt) {
      newErrors.publishedAt = 'Publish date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);

    try {
      const newsletterData = {
        title: formData?.title,
        excerpt: formData?.excerpt,
        content: formData?.content,
        status: formData?.status,
        slug: formData?.slug,
        tags: formData?.tags,
        publishedAt: formData?.publishedAt || null,
        updated_at: new Date()?.toISOString(),
      };

      if (onSave) {
        await onSave(newsletterData);
      }

      try {
        await generateSitemap();
        console.log('Sitemap regenerated successfully');
      } catch (sitemapError) {
        console.error('Error regenerating sitemap:', sitemapError);
        // Don't fail the whole operation if sitemap generation fails
      }
    } catch (err) {
      console.error('Error saving newsletter:', err);
      setError(err?.message || 'Failed to save newsletter');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <Input
          type="text"
          value={formData?.title}
          onChange={handleTitleChange}
          placeholder="Enter newsletter title"
          className={errors?.title ? 'border-red-500' : ''}
        />
        {errors?.title && (
          <p className="mt-1 text-sm text-red-500">{errors?.title}</p>
        )}
      </div>
      {/* Slug */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL Slug <span className="text-red-500">*</span>
        </label>
        <Input
          type="text"
          value={formData?.slug}
          onChange={handleSlugChange}
          placeholder="url-friendly-slug"
          className={errors?.slug ? 'border-red-500' : ''}
        />
        {errors?.slug && (
          <p className="mt-1 text-sm text-red-500">{errors?.slug}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Auto-generated from title. You can edit it manually.
        </p>
      </div>
      {/* Summary */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Summary
        </label>
        <textarea
          value={formData?.excerpt}
          onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e?.target?.value }))}
          placeholder="Brief summary of the newsletter"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
      </div>
      {/* Content */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Content
        </label>
        
        <RichTextEditor
          id="editor-content-textarea"
          value={formData?.content}
          onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
          placeholder="Newsletter content (supports rich text formatting). Use the image button in the toolbar to upload images."
          disabled={isSubmitting}
        />
      </div>
      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formData?.tags?.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
            >
              <TagIcon className="w-3 h-3" />
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 hover:text-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <Input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e?.target?.value)}
          onKeyDown={handleAddTag}
          placeholder="Type tag and press Enter or comma"
        />
        <p className="mt-1 text-xs text-gray-500">
          Press Enter or comma to add tags
        </p>
      </div>
      {/* Publish Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Publish Date <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="date"
            value={formData?.publishedAt}
            onChange={(e) => setFormData(prev => ({ ...prev, publishedAt: e?.target?.value }))}
            className={`pl-10 ${errors?.publishedAt ? 'border-red-500' : ''}`}
          />
        </div>
        {errors?.publishedAt && (
          <p className="mt-1 text-sm text-red-500">{errors?.publishedAt}</p>
        )}
      </div>
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center gap-3 pt-4 border-t">
        <Button
          type="submit"
          disabled={isSaving || isSubmitting}
          className="flex-1"
        >
          <Save className="w-4 h-4 mr-2" />
          {(isSaving || isSubmitting) ? 'Saving...' : newsletter ? 'Update Newsletter' : 'Create Newsletter'}
        </Button>
        {newsletter && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving || isSubmitting}
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default NewsletterEditorForm;